import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Filter, X, EyeOff, Maximize2, Minimize2, Layout, Loader2, Check, ArrowRightLeft } from 'lucide-react';
import { createFinancialBudget, updateFinancialBudget } from '../../utils/budgetApi';
import { formatCurrencyRounded } from '../../utils/currency';

const PnLBudgetView = ({
  chartOfAccounts = [],
  budgetData = [],
  orgId,
  orgCode,
  locationId,
  currency = 'USD',
  year,
  onDataChange,
  initialStartMonth = 0,
  initialEndMonth = 11
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [savingCells, setSavingCells] = useState(new Set());
  const [savedCells, setSavedCells] = useState(new Set()); // Track cells that were successfully saved
  const [startMonth, setStartMonth] = useState(initialStartMonth); // 0-11 for Jan-Dec
  const [endMonth, setEndMonth] = useState(initialEndMonth); // 0-11 for Jan-Dec
  const [hideZeroBalances, setHideZeroBalances] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [density, setDensity] = useState('comfortable'); // 'compact', 'comfortable'
  const [localEdits, setLocalEdits] = useState({}); // { `${accountId}_${monthIndex}`: value }
  const saveTimerRef = useRef({});

  // Sync date range from parent when it changes (only if different)
  useEffect(() => {
    if (startMonth !== initialStartMonth) {
      setStartMonth(initialStartMonth);
    }
    if (endMonth !== initialEndMonth) {
      setEndMonth(initialEndMonth);
    }
  }, [initialStartMonth, initialEndMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Month names for column headers
  const allMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Filtered month names and indices based on date range
  const filteredMonthIndices = useMemo(() => {
    const indices = [];
    for (let i = startMonth; i <= endMonth; i++) {
      indices.push(i);
    }
    return indices;
  }, [startMonth, endMonth]);

  // Group accounts hierarchically: AccountType > Department > Accounts
  const groupedAccounts = useMemo(() => {
    const groups = {};
    const budgetMap = {};

    // Map budget data for easy lookup: chartOfAccountId -> month -> entry
    budgetData.forEach(entry => {
      if (!entry.chartOfAccountId) return;
      
      const date = new Date(entry.budgetDate);
      // Ensure we match the year correctly
      if (date.getUTCFullYear() !== year) return;

      const monthIndex = date.getUTCMonth();
      
      if (!budgetMap[entry.chartOfAccountId]) {
        budgetMap[entry.chartOfAccountId] = {};
      }
      budgetMap[entry.chartOfAccountId][monthIndex] = entry;
    });

    chartOfAccounts.forEach(account => {
      const accountType = account.accountType || 'Uncategorized';

      // Only include Revenue, Expense, and Statistics/Statistical accounts in P&L view by default
      // Statistics accounts are displayed but do NOT affect Net Income calculation
      const isStatisticsAccount = accountType === 'Statistics' || accountType === 'Statistical';
      if (accountType !== 'Revenue' && accountType !== 'Expense' && !isStatisticsAccount) {
        return;
      }

      // Filter out accounts with null department (except Statistics accounts which may not have departments)
      if (!account.department && !isStatisticsAccount) {
        return;
      }

      // Use accountType as department for Statistics accounts without a department
      const department = account.department || (isStatisticsAccount ? 'Statistical Accounts' : null);
      if (!department) {
        return;
      }

      // Initialize account type group
      if (!groups[accountType]) {
        groups[accountType] = {
          type: accountType,
          departments: {},
          monthlyTotals: Array(12).fill(0),
          filteredMonthlyTotals: []
        };
      }

      // Initialize department group
      if (!groups[accountType].departments[department]) {
        groups[accountType].departments[department] = {
          name: department,
          accounts: [],
          monthlyTotals: Array(12).fill(0),
          filteredMonthlyTotals: []
        };
      }

      // Get budget data for this account
      const accountBudgets = budgetMap[account.id] || {};
      const monthlyAmounts = [];
      const monthlyIds = [];

      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const entry = accountBudgets[monthIndex];
        const rawAmount = entry ? (entry.budgetAmount || 0) : 0;
        
        // Naturalize amount based on account type
        // Revenue: stored as negative (credit), display as positive. Positive (debit) is negative revenue.
        // Expense: stored as positive (debit).
        // Wait, normally in DB:
        // Revenue (Credit) = Negative
        // Expense (Debit) = Positive
        // In P&L view:
        // Revenue is shown as Positive
        // Expense is shown as Positive (usually, then subtracted for Net Income)
        
        // Let's stick to the logic in PnLForecastView:
        // Budget: For expenses, always multiply by -1 since budgets are stored as negative (Wait, PnLForecastView said budgets are stored as negative for expense??)
        // Let's re-read PnLForecastView logic carefully.
        
        /* 
           PnLForecastView logic:
           const budget = accountType === 'Expense'
             ? rawBudget * -1
             : (accountType === 'Revenue' && rawBudget < 0)
               ? Math.abs(rawBudget)
               : rawBudget;
        */
        
        // Assuming standard accounting signs in DB (Revenue -, Expense +)
        // If PnLForecastView flips Expense, it implies Expense might be stored as negative in some contexts or it wants to display it as negative? 
        // Actually, usually Expenses are displayed as positive numbers in a P&L, but mathematically subtracted from Revenue.
        
        // Let's assume standard display: Positive numbers for both Revenue and Expense.
        // If DB has Revenue as -1000, display 1000.
        // If DB has Expense as 500, display 500.
        
        let displayAmount = rawAmount;
        
        // Adjust display amount sign if needed. 
        // We'll trust the raw amount for now, but if we see weird signs we can adjust.
        // The previous BudgetTable just displayed raw values.
        
        monthlyAmounts.push(displayAmount);
        monthlyIds.push(entry ? entry.id : null);

        // Update totals
        groups[accountType].departments[department].monthlyTotals[monthIndex] += displayAmount;
        groups[accountType].monthlyTotals[monthIndex] += displayAmount;
      }
      
      // Calculate total for the range
      const totalInRange = filteredMonthIndices.reduce((sum, monthIdx) => 
        sum + (monthlyAmounts[monthIdx] || 0), 0
      );

      // Add account with its monthly data
      const accountData = {
        ...account,
        monthlyAmounts,
        monthlyIds,
        totalInRange
      };

      groups[accountType].departments[department].accounts.push(accountData);
    });

    // Calculate filtered monthly totals
    Object.values(groups).forEach(typeGroup => {
      typeGroup.filteredMonthlyTotals = filteredMonthIndices.map(monthIdx => 
        typeGroup.monthlyTotals[monthIdx] || 0
      );

      Object.values(typeGroup.departments).forEach(deptGroup => {
        deptGroup.filteredMonthlyTotals = filteredMonthIndices.map(monthIdx => 
          deptGroup.monthlyTotals[monthIdx] || 0
        );

        // Filter zero balances if needed
        if (hideZeroBalances) {
          deptGroup.accounts = deptGroup.accounts.filter(acc => acc.totalInRange !== 0);
        }

        // Sort accounts alphabetically
        deptGroup.accounts.sort((a, b) => {
          const nameA = (a.accountName || '').toLowerCase();
          const nameB = (b.accountName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      });
    });

    // Sort account types: Revenue first, then Expenses, then Statistics/Statistical
    const sortedTypes = Object.keys(groups).sort((a, b) => {
      const order = { 'Revenue': 0, 'Expense': 1, 'Statistics': 2, 'Statistical': 2 };
      const aOrder = order[a] ?? 3;
      const bOrder = order[b] ?? 3;
      return aOrder - bOrder || a.localeCompare(b);
    });

    return { groups, sortedTypes };
  }, [chartOfAccounts, budgetData, year, filteredMonthIndices, hideZeroBalances]);

  // Expand/Collapse All Handlers
  const expandAll = useCallback(() => {
    const newExpanded = {};
    groupedAccounts.sortedTypes.forEach(type => {
      newExpanded[type] = true;
      Object.keys(groupedAccounts.groups[type].departments).forEach(dept => {
        newExpanded[`${type}-${dept}`] = true;
      });
    });
    setExpandedSections(newExpanded);
  }, [groupedAccounts]);

  const collapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  // Expand to department level (default view) - shows departments but not individual accounts
  const expandToDepartmentLevel = useCallback(() => {
    const newExpanded = {};
    groupedAccounts.sortedTypes.forEach(type => {
      newExpanded[type] = true; // Expand account types (Revenue, Expense)
      // Don't expand departments - this keeps individual accounts hidden
    });
    setExpandedSections(newExpanded);
  }, [groupedAccounts]);

  // Set initial expansion to department level on mount
  React.useEffect(() => {
    if (groupedAccounts.sortedTypes.length > 0) {
      expandToDepartmentLevel();
    }
  }, [groupedAccounts.sortedTypes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate net income (Revenue - Expenses)
  const netIncomeMonthly = useMemo(() => {
    const revenueMonthly = groupedAccounts.groups['Revenue']?.filteredMonthlyTotals || [];
    const expensesMonthly = groupedAccounts.groups['Expense']?.filteredMonthlyTotals || [];
    
    // Map indices correctly from filteredMonthIndices
    return filteredMonthIndices.map((_, i) => {
      // revenueMonthly[i] corresponds to the i-th visible month
      return (revenueMonthly[i] || 0) - (expensesMonthly[i] || 0);
    });
  }, [groupedAccounts, filteredMonthIndices]);

  const totalNetIncome = useMemo(() => {
    return netIncomeMonthly.reduce((sum, val) => sum + val, 0);
  }, [netIncomeMonthly]);

  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  // Handle inline cell edit
  const handleCellEdit = useCallback(async (account, monthIndex, newValue) => {
    const cellKey = `${account.id}_${monthIndex}`;
    
    // Update local state immediately
    setLocalEdits(prev => ({ ...prev, [cellKey]: newValue }));
    
    // Show saving indicator
    setSavingCells(prev => new Set([...prev, cellKey]));
    
    // Clear existing timer
    if (saveTimerRef.current[cellKey]) {
      clearTimeout(saveTimerRef.current[cellKey]);
    }
    
    // Set new timer
    saveTimerRef.current[cellKey] = setTimeout(async () => {
      try {
        const budgetDate = new Date(Date.UTC(year, monthIndex, 1));
        const entryId = account.monthlyIds[monthIndex];
        
        const payload = {
            orgCode,
            orgId, 
            locationId,
            chartOfAccountId: account.id,
            budgetDate: budgetDate.toISOString().split('T')[0],
            budgetAmount: newValue,
            inputCurrency: currency,
            inputAmount: newValue,
            calculationFormula: '', // Clear formula on direct edit
            accountNumber: account.accountNumber // Sometimes needed
        };

        // We need orgCode. Parent passes `orgCode` to `BudgetTable`. We should add it to props.
        // Assuming `orgId` and `orgCode` are available or handled. 
        // Let's assume we need to update props to include `orgCode`.

        if (entryId) {
            payload.id = entryId;
            await updateFinancialBudget(payload);
        } else {
            await createFinancialBudget(payload);
        }

        // Notify parent to refresh data
        if (onDataChange) {
            onDataChange();
        }
        
        // Mark as saved
        setSavedCells(prev => new Set([...prev, cellKey]));
        setSavingCells(prev => {
          const updated = new Set(prev);
          updated.delete(cellKey);
          return updated;
        });
        
        setTimeout(() => {
          setSavedCells(prev => {
            const updated = new Set(prev);
            updated.delete(cellKey);
            return updated;
          });
        }, 2000);
        
      } catch (error) {
        console.error('Error saving budget:', error);
        setSavingCells(prev => {
          const updated = new Set(prev);
          updated.delete(cellKey);
          return updated;
        });
      } finally {
        delete saveTimerRef.current[cellKey];
      }
    }, 800);
  }, [year, orgId, orgCode, locationId, currency, onDataChange]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return formatCurrencyRounded(0, currency);
    const formatted = formatCurrencyRounded(Math.abs(value), currency);
    return value < 0 ? `(${formatted})` : formatted;
  };

  return (
    <div className="flex flex-col h-full bg-white border rounded-lg overflow-hidden">
      {/* Compact Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/50">
        {/* Left: View Controls */}
        <div className="flex items-center gap-2">
          {/* Auto-save Indicator */}
          <div className="flex items-center px-2 py-0.5 bg-white rounded border border-gray-100">
            {savingCells.size > 0 ? (
              <div className="flex items-center gap-1 text-[10px] text-orange-600 font-medium">
                <Loader2 size={10} className="animate-spin" />
                <span>Saving</span>
              </div>
            ) : savedCells.size > 0 ? (
              <div className="flex items-center gap-1 text-[10px] text-green-600 font-medium">
                <Check size={10} />
                <span>Saved</span>
              </div>
            ) : (
              <span className="text-[10px] text-gray-400 font-medium">Synced</span>
            )}
          </div>

          <div className="h-4 w-px bg-gray-200" />

          {/* Expand/Collapse */}
          <div className="flex items-center gap-0.5 bg-white rounded border border-gray-200">
            <button
              onClick={expandAll}
              className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-l transition-colors"
              title="Expand All"
            >
              <Maximize2 size={12} />
            </button>
            <button
              onClick={collapseAll}
              className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-r transition-colors"
              title="Collapse All"
            >
              <Minimize2 size={12} />
            </button>
          </div>

          {/* Density Toggle */}
          <button
            onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}
            className={`p-1 rounded border transition-colors ${
              density === 'compact'
                ? 'bg-orange-50 text-orange-600 border-orange-200'
                : 'bg-white text-gray-500 border-gray-200 hover:text-gray-900 hover:bg-gray-50'
            }`}
            title={density === 'compact' ? "Comfortable View" : "Compact View"}
          >
            <Layout size={12} />
          </button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Filter Controls */}
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-xs font-medium hover:bg-gray-50 transition-colors text-gray-600"
            >
              <Filter size={12} />
              <span className="hidden sm:inline">Filters</span>
              {(startMonth !== 0 || endMonth !== 11 || hideZeroBalances) && (
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-500 text-white text-[8px] font-bold">
                  {[startMonth !== 0 || endMonth !== 11, hideZeroBalances].filter(Boolean).length}
                </span>
              )}
            </button>
            {showFilterDropdown && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-4 animate-in fade-in zoom-in-95 duration-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">View Options</h3>
                  <button
                    onClick={() => setShowFilterDropdown(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X size={14} />
                  </button>
                </div>

                {/* Date Range Filter */}
                <div className="mb-5">
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Date Range</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <select
                        value={startMonth}
                        onChange={(e) => {
                          const newStart = parseInt(e.target.value);
                          setStartMonth(newStart);
                          if (newStart > endMonth) {
                            setEndMonth(newStart);
                          }
                        }}
                        className="w-full pl-2 pr-6 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
                      >
                        {allMonthNames.map((month, idx) => (
                          <option key={idx} value={idx}>{month}</option>
                        ))}
                      </select>
                      <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    <span className="text-gray-400 text-xs">to</span>
                    <div className="relative flex-1">
                      <select
                        value={endMonth}
                        onChange={(e) => {
                          const newEnd = parseInt(e.target.value);
                          setEndMonth(newEnd);
                          if (newEnd < startMonth) {
                            setStartMonth(newEnd);
                          }
                        }}
                        className="w-full pl-2 pr-6 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none bg-white"
                      >
                        {allMonthNames.map((month, idx) => (
                          <option key={idx} value={idx}>{month}</option>
                        ))}
                      </select>
                      <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Hide Zero Balances Toggle */}
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                    <div className={`p-1.5 rounded-md ${hideZeroBalances ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
                      <EyeOff size={14} />
                    </div>
                    Hide Zero Balance Accounts
                  </label>
                  <button
                    onClick={() => setHideZeroBalances(!hideZeroBalances)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      hideZeroBalances ? 'bg-orange-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                        hideZeroBalances ? 'translate-x-4.5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Clear Filters Button */}
                {(startMonth !== 0 || endMonth !== 11 || hideZeroBalances) && (
                  <button
                    onClick={() => {
                      setStartMonth(0);
                      setEndMonth(11);
                      setHideZeroBalances(false);
                    }}
                    className="w-full py-2 text-xs text-gray-600 hover:text-gray-900 font-medium bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Reset to Default
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Net Income Display */}
          <div className="flex items-center gap-1.5 pl-2 ml-1 border-l border-gray-200">
            <span className="text-[10px] text-gray-400 font-medium uppercase">Net:</span>
            <span className={`text-sm font-bold ${totalNetIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(totalNetIncome)}
            </span>
          </div>
        </div>
      </div>

      {/* P&L Table */}
      <div className="flex-1 overflow-auto bg-white relative">
        <table className="min-w-full border-collapse text-[13px]">
          <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b border-r border-gray-200 sticky left-0 bg-gray-50 z-30 w-[280px] min-w-[200px]">
                Account
              </th>
              {filteredMonthIndices.map((monthIdx, displayIdx) => (
                <th
                  key={displayIdx}
                  className="text-center px-1 py-1.5 font-semibold text-gray-700 border-b border-r border-gray-200"
                >
                  <div className="flex flex-col items-center justify-center gap-0.5">
                    <span className="uppercase tracking-wide text-xs">{allMonthNames[monthIdx]}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white">
            {groupedAccounts.sortedTypes.map(accountType => {
              const typeData = groupedAccounts.groups[accountType];
              const isTypeExpanded = expandedSections[accountType];

              return (
                <React.Fragment key={accountType}>
                  {/* Account Type Header */}
                  <tr
                    className={`bg-gray-100 hover:bg-gray-200/80 cursor-pointer border-b border-gray-200 transition-colors ${
                      density === 'compact' ? 'h-7 text-xs' : 'h-9 text-sm'
                    }`}
                    onClick={() => toggleSection(accountType)}
                  >
                    <td className={`font-bold text-gray-800 flex items-center sticky left-0 bg-gray-100 z-10 border-r border-gray-300 pl-3 ${
                      density === 'compact' ? 'py-1' : 'py-2'
                    }`}>
                      <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
                         <div className="text-gray-400">
                           {isTypeExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                         </div>
                         <span className="truncate">{accountType}</span>
                      </div>
                    </td>
                    {filteredMonthIndices.map((monthIdx, displayIdx) => {
                      const amount = typeData.monthlyTotals[monthIdx];
                      const cellClass = `px-1.5 text-right font-bold text-gray-900 border-r border-gray-300 ${
                        density === 'compact' ? 'py-1 text-[11px]' : 'py-2 text-xs'
                      }`;
                      return (
                        <td key={displayIdx} className={cellClass}>
                          {formatCurrency(amount)}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Departments */}
                  {isTypeExpanded && Object.entries(typeData.departments).map(([deptName, deptData]) => {
                    const deptKey = `${accountType}-${deptName}`;
                    const isDeptExpanded = expandedSections[deptKey];

                    return (
                      <React.Fragment key={deptKey}>
                        {/* Department Header */}
                        <tr
                          className={`bg-gray-50 hover:bg-gray-100 cursor-pointer border-b border-gray-200/80 transition-colors ${
                            density === 'compact' ? 'h-6 text-[11px]' : 'h-8 text-xs'
                          }`}
                          onClick={() => toggleSection(deptKey)}
                        >
                          <td className={`font-semibold text-gray-700 flex items-center sticky left-0 bg-gray-50 z-10 border-r border-gray-200 pl-6 ${
                            density === 'compact' ? 'py-0.5' : 'py-1.5'
                          }`}>
                            <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
                              <div className="text-gray-400">
                                {isDeptExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </div>
                              <span className="truncate">{deptName}</span>
                            </div>
                          </td>
                          {filteredMonthIndices.map((monthIdx, displayIdx) => {
                            const amount = deptData.monthlyTotals[monthIdx];
                            const cellClass = `px-1.5 text-right font-semibold text-gray-700 border-r border-gray-200 ${
                              density === 'compact' ? 'py-0.5 text-[10px]' : 'py-1 text-[11px]'
                            }`;
                            return (
                              <td key={displayIdx} className={cellClass}>
                                {formatCurrency(amount)}
                              </td>
                            );
                          })}
                        </tr>

                        {/* Accounts */}
                        {isDeptExpanded && deptData.accounts.map(account => {
                          return (
                            <tr
                              key={account.id}
                              className={`group hover:bg-orange-50/30 transition-colors border-b border-gray-100 ${
                                density === 'compact' ? 'h-6 text-[11px]' : 'h-8 text-[12px]'
                              }`}
                            >
                              <td className={`text-gray-600 sticky left-0 bg-white group-hover:bg-orange-50/30 z-10 border-r border-gray-200 pl-10 ${
                                density === 'compact' ? 'py-0.5' : 'py-1'
                              }`}>
                                <div className="flex flex-col justify-center min-w-0">
                                  <div className="font-medium truncate text-gray-700">{account.accountName}</div>
                                  <div className="text-[9px] text-gray-400 font-mono leading-none mt-0.5">{account.accountNumber}</div>
                                </div>
                              </td>
                              {filteredMonthIndices.map((monthIdx, displayIdx) => {
                                const amount = account.monthlyAmounts[monthIdx];
                                const cellKey = `${account.id}_${monthIdx}`;
                                const displayValue = localEdits[cellKey] !== undefined ? localEdits[cellKey] : amount;
                                const isSaving = savingCells.has(cellKey);
                                const isSaved = savedCells.has(cellKey);

                                const baseCellClass = `px-1.5 text-right border-r border-gray-100 group-hover:border-gray-200 relative p-0 ${
                                  density === 'compact' ? 'py-0.5' : 'py-1'
                                }`;

                                return (
                                  <td key={displayIdx} className={baseCellClass}>
                                    <div className="h-full w-full relative">
                                        <input
                                            type="number"
                                            value={displayValue}
                                            onChange={(e) => handleCellEdit(account, monthIdx, parseFloat(e.target.value) || 0)}
                                            onFocus={(e) => {
                                                if (parseFloat(e.target.value) === 0) setLocalEdits(prev => ({ ...prev, [cellKey]: '' }));
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '') handleCellEdit(account, monthIdx, 0);
                                            }}
                                            className={`w-full h-full text-right px-1.5 bg-transparent hover:bg-white focus:bg-white border-transparent focus:border-b-2 focus:border-orange-500 focus:ring-0 transition-colors font-medium text-gray-800 ${
                                                isSaving ? 'text-orange-600' : isSaved ? 'text-green-600' : ''
                                            } [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                        />
                                        {isSaving && <Loader2 size={8} className="absolute top-1/2 left-1 -translate-y-1/2 animate-spin text-orange-400 pointer-events-none" />}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Net Income Row */}
            <tr className="bg-white border-t-2 border-gray-300 font-bold sticky bottom-0 z-40 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
              <td className={`text-gray-900 sticky left-0 bg-white z-40 border-r border-gray-200 pl-4 ${
                density === 'compact' ? 'py-1.5 text-xs' : 'py-3 text-sm'
              }`}>
                Net Income
              </td>
              {filteredMonthIndices.map((monthIdx, displayIdx) => {
                const amount = netIncomeMonthly[displayIdx];
                const cellClass = `text-right px-1.5 border-r border-gray-200 ${
                  density === 'compact' ? 'py-1.5 text-xs' : 'py-3 text-sm'
                }`;
                return (
                  <td key={displayIdx} className={`${cellClass} ${amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(amount)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default React.memo(PnLBudgetView);
